// DEPENDENCIES
import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";

// MSAL
import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useIsAuthenticated,
	useMsal,
} from "@azure/msal-react";
import { getAPICall } from "../../config/apiCalls";

// CSS
import "./App.css";
import j2logo from "../../images/j2_logo_full.png";
import j2logoWhite from "../../images/j2_logo_white.png";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ThemeProvider } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";

// CUSTOM COMPONENTS
// Various
import Login from "../Login/Login";
import Navigation from "../Navigation/Navigation";

import J2PageNotFound from "../Misc/J2PageNotFound/J2PageNotFound";

// Proposals
import RFP from "../Proposals/RFP/RFP";
import RFA from "../Proposals/RFA/RFA";
import ProductLibrary from "../Proposals/ProductLibrary/ProductLibrary";

// Project Coordinaiton
import PendingJobs from "../Proposals/PendingJobs/PendingJobs";
import PendingJobReviewScreen from "../Proposals/PendingJobs/PendingJobReviewScreen/PendingJobReviewScreen";
import PendingAmendments from "../Proposals/PendingAmendments/PendingAmendments";
import PendingAmendmentReviewScreen from "../Proposals/PendingAmendments/PendingAmendmentReviewScreen/PendingAmendmentReviewScreen";
import PostAmendment from "../Proposals/PendingAmendments/PostAmendment/PostAmendment";
import ArchiveManager from "../Archives/ArchiveManager/ArchiveManager";
import ProjectViewer from "../Projects/ProjectViewer/ProjectViewer";
import PropertyViewer from "../Properties/PropertyViewer/PropertyViewer";
import WorkloadViewer from "../WorkloadViewer/WorkloadViewer";
import ProjectHealthDashboard from "../ProjectHealthDashboard/ProjectHealthDashboard";
import PMProjectDashboard from "../PMProjectDashboard/PMProjectDashboard";
import TimeEntry from "../TimeEntry/TimeEntry";

// Logs
import AccessLogsDashboard from "../Logs/AccessLogsDashboard/AccessLogsDashboard";
import AutomationLogsDashboard from "../Logs/AutomationLogsDashboard/AutomationLogsDashboard";
import RFPFormLog from "../Logs/RFPFormLog/RFPFormLog";
import RFAFormLog from "../Logs/RFAFormLog/RFAFormLog";
import RFPUserFormLog from "../Logs/RFPUserFormLog/RFPUserFormLog";
import RFAUserFormLog from "../Logs/RFAUserFormLog/RFAUserFormLog";
import PendingJobFormLog from "../Logs/PendingJobFormLog/PendingJobFormLog";
import PendingAmendmentFormLog from "../Logs/PendingAmendmentFormLog/PendingAmendmentFormLog";

import DropboxInterrogator from "../DropboxInterrogator/DropboxInterrogator";
import UnitMiningDashboard from "../UnitMining/UnitMiningDashboard";

// Admin
import CategoryConsole from "../Admin/CategoryConsole/CategoryConsole";
import EmployeeViewer from "../Admin/EmployeeViewer/EmployeeViewer";
import RoleConsole from "../Admin/RoleConsole/RoleConsole";
import JobTitlesConsole from "../Admin/JobTitlesConsole/JobTitlesConsole";
import PropertyConsole from "../Admin/PropertyConsole/PropertyConsole";
import DeviceStatusesConsole from "../Admin/DeviceStatusesConsole/DeviceStatusesConsole";
import ProjectConsole from "../Admin/ProjectConsole/ProjectConsole";
import PhaseConsole from "../Admin/PhaseConsole/PhaseConsole";
import WorkloadViewerConsole from "../Admin/WorkloadViewerConsole/WorkloadViewerConsole";
import TimeOffConsole from "../Admin/TimeOffConsole/TimeOffConsole";

// Detail Library
import DetailLibrary from "../DetailLibrary/DetailLibrary";
import DetailScreen from "../DetailLibrary/DetailScreen/DetailScreen";

// Assemblies
import Assemblies from "../Assemblies/Assemblies";
import DesignCategories from "../DesignCategories/DesignCategories";
import BuildingElements from "../BuildingElements/BuildingElements";

// Specifications
import SpecLibrary from "../Specifications/SpecLibrary/SpecLibrary";
import SpecDetailScreen from "../Specifications/SpecLibrary/SpecDetailScreen/SpecDetailScreen";
import DesignJobs from "../Specifications/DesignJobs/DesignJobs";
import ScopeOfWork from "../ScopeOfWork/ScopeOfWork";
import DesignJobDetailScreen from "../Specifications/DesignJobs/DesignJobDetailScreen/DesignJobDetailScreen";
import PendingChanges from "../Specifications/PendingChanges/PendingChanges";
import PendingSpecChangeReviewScreen from "../Specifications/PendingChanges/PendingSpecChangeReviewScreen/PendingSpecChangeReviewScreen";
import DesignJobSpecificationEdit from "../Specifications/DesignJobs/DesignJobDetailScreen/DesignJobSpecificationEdit/DesignJobSpecificationEdit";
import SpecificationChangeLog from "../Specifications/SpecificationChangeLog/SpecificationChangeLog";
import DesignJobChangeLog from "../Specifications/DesignJobChangeLog/DesignJobChangeLog";

// CRS
import CoreRepairScopes from "../CoreRepairScopes/CoreRepairScopes";

// Hardware Inventory
import Devices from "../HardwareInventory/Devices/Devices";
import DeviceLeases from "../HardwareInventory/DeviceLeases/DeviceLeases";
import ServiceHistory from "../HardwareInventory/ServiceHistory/ServiceHistory";

// Profile
import EmailSignature from "../Profile/EmailSignature/EmailSignature";

// CONSTANTS
import { theme } from "../../constants/theme";
import { UserPreferencesProvider } from "../../context/UserPreferencesContext";

// Page titles shown in the mobile top bar (the pages allowed on mobile)
const MOBILE_PAGE_TITLES = {
	"/": "RFP",
	"/rfa": "RFA",
	"/my-time-entry": "My Time Entry",
};

// REACT COMPONENT
function App() {
	// MSAL
	const { instance, accounts } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const location = useLocation();
	const navigate = useNavigate();
	const isMobile = useMediaQuery("(max-width: 767px)");

	// STATES
	// User States
	const [accessibleTools, setAccessibleTools] = useState([]);

	// Context States
	const [employeeID, setEmployeeID] = useState(0);

	// Feedback States
	const [isLoading, setIsLoading] = useState(false);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const mobileAllowedPaths = useMemo(
		() => new Set(["/", "/rfa", "/my-time-entry"]),
		[],
	);

	// USE EFFECT
	useEffect(() => {
		if (accounts.length > 0) {
			fetchUserInfo();
		}
	}, [accounts]);

	useEffect(() => {
		if (isMobile) {
			setMobileNavOpen(false);
		}
	}, [location.pathname, isMobile]);

	useEffect(() => {
		if (!isMobile || isLoading || !isAuthenticated) {
			return;
		}

		const normalizedPath =
			location.pathname.replace(/\/+$/, "") || "/";

		if (!mobileAllowedPaths.has(normalizedPath)) {
			navigate("/", { replace: true });
		}
	}, [
		isMobile,
		isLoading,
		isAuthenticated,
		location.pathname,
		mobileAllowedPaths,
		navigate,
	]);

	// FETCH USER INFO FUNCTIONS
	const fetchUserInfo = async () => {
		setIsLoading(true);

		try {
			const idRes = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/idFromEmail",
				{ email: accounts[0].username }
			);
			const employee = idRes?.data?.[0];
			if (!employee?.id) {
				throw new Error("No employee record found for current user.");
			}
			setEmployeeID(employee.id);

			await fetchAccessibleTools(employee.id);
		} catch (error) {
			console.error("App.js - fetchUserInfo - error:", error);

			// Log out user if error
			instance.logoutRedirect({
				postLogoutRedirectUri: "/",
				onRedirectNavigate: (url) => {
					// Return false if you would like to stop navigation after local logout
					return false;
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	const fetchAccessibleTools = async (employeeID) => {
		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/tools",
				{ employeeID: employeeID }
			);
			setAccessibleTools(response.data);

			return;
		} catch (error) {
			console.error("Error fetching accessible tools", error);
		}
	};

	// LOADING
	if (isLoading) {
		return (
			<div className="loading">
				<img alt="j2 logo" src={j2logo} id="loading-logo" />
				<CircularProgress />
			</div>
		);
	}

	// STANDARD
	return (
		<>
			<AuthenticatedTemplate>
				<ThemeProvider theme={theme}>
					<UserContext.Provider value={employeeID}>
						<UserPreferencesProvider>
							<div className="App" id="app">
								{isMobile && (
									<div className="mobileTopBar">
										<IconButton
											className="mobileMenuButton"
											aria-label="Open navigation"
											color="inherit"
											onClick={() =>
												setMobileNavOpen(true)
											}
										>
											<MenuIcon />
										</IconButton>
										<img
											alt="j2 logo"
											src={j2logoWhite}
											className="mobileTopBarLogo"
										/>
										<span className="mobileTopBarTitle">
											{MOBILE_PAGE_TITLES[
												location.pathname.replace(/\/+$/, "") || "/"
											] || ""}
										</span>
									</div>
								)}
								{!isMobile && (
									<div className="nav">
										<Navigation accessibleTools={accessibleTools} />
									</div>
								)}
								<Drawer
									anchor="left"
									open={isMobile && mobileNavOpen}
									onClose={() => setMobileNavOpen(false)}
									ModalProps={{ keepMounted: true }}
										PaperProps={{
											className: "mobileNavDrawer",
											sx: {
												backgroundColor: "#222222",
												color: "#ffffff",
												width: "min(50vw, 180px)",
											},
										}}
									>
									<Navigation
										accessibleTools={accessibleTools}
										forceExpanded
										disableHover
										mobileOnly
									/>
								</Drawer>
								<div className="app">
									<Routes>
										{/* Proposals */}
										<Route
											path="/"
											element={<RFP />}
										></Route>
										<Route
											path="/rfa"
											element={<RFA />}
										></Route>
										{/* My Time Entry — every employee */}
										<Route
											path="/my-time-entry"
											element={
												<TimeEntry
													hasProjectViewer={accessibleTools.some(
														(t) => t.name === "Project Viewer",
													)}
													isAdmin={Boolean(
														accessibleTools.find(
															(t) => t.name === "My Time Entry",
														)?.admin_access,
													)}
												/>
											}
										></Route>
										<Route
											path="/logs/rfp_user_form"
											element={<RFPUserFormLog />}
										></Route>
										<Route
											path="/logs/rfa_user_form"
											element={<RFAUserFormLog />}
										></Route>
										{/* Product Library: the "Product Library" tool, or Admin. Editing needs that tool's admin access (checked on the server). */}
										{accessibleTools.some(
											(t) => t.name === "Product Library" || t.name === "Admin",
										) && (
											<Route
												path="/proposals/product-library"
												element={<ProductLibrary />}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Pending Jobs")
										) && (
											<>
												<Route
													path="/jobs/pending"
													element={<PendingJobs />}
												></Route>
												<Route
													path="/jobs/pending/:id"
													element={
														<PendingJobReviewScreen />
													}
												></Route>
												<Route
													path="/jobs/amendments/pending"
													element={
														<PendingAmendments />
													}
												></Route>
												<Route
													path="/jobs/amendments/pending/:id"
													element={
														<PendingAmendmentReviewScreen />
													}
												></Route>
											</>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Post Amendments")
										) && (
											<Route
												path="/jobs/amendments/post"
												element={<PostAmendment />}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Project Viewer")
										) && (
											<Route
												path="/projects/viewer"
												element={
													<ProjectViewer
														isAdmin={Boolean(
															accessibleTools.find(
																(t) =>
																	t.name ===
																	"Project Viewer"
															)?.admin_access
														)}
														hasDesignAccess={Boolean(
															accessibleTools
																.map((t) => t.name)
																.includes("Spec Library")
														)}
													/>
												}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Property Viewer")
										) && (
											<Route
												path="/properties/viewer"
												element={
													<PropertyViewer
														isAdmin={Boolean(
															accessibleTools.find(
																(t) =>
																	t.name ===
																	"Property Viewer"
															)?.admin_access
														)}
													/>
												}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Workload Viewer")
										) && (
											<Route
												path="/workload/viewer"
												element={<WorkloadViewer />}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Project Health Dashboard")
										) && (
											<Route
												path="/projects/health"
												element={<ProjectHealthDashboard />}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("PM Project Dashboard")
										) && (
											<Route
												path="/projects/pm-dashboard/:employeeId"
												element={
													<PMProjectDashboard
														isAdmin={Boolean(
															accessibleTools.find(
																(t) =>
																	t.name ===
																	"PM Project Dashboard"
															)?.admin_access
														)}
													/>
												}
											></Route>
										)}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Archive Manager")
										) && (
											<Route
												path="/archives/manager"
												element={<ArchiveManager />}
											></Route>
										)}

										{/* Logs */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Logs")
										) && (
											<>
												<Route
													path="/logs/access"
													element={<AccessLogsDashboard />}
												></Route>
												<Route
													path="/logs/automation/dashboard"
													element={
														<AutomationLogsDashboard />
													}
												></Route>
												<Route
													path="/logs/rfp_form"
													element={<RFPFormLog />}
												></Route>
												<Route
													path="/logs/rfa_form"
													element={<RFAFormLog />}
												></Route>
												<Route
													path="/logs/pending_job_form"
													element={<PendingJobFormLog />}
												></Route>
												<Route
													path="/logs/pending_amendment_form"
													element={<PendingAmendmentFormLog />}
												></Route>
											</>
										)}

										{/* Admin */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Admin")
										) && (
											<>
												<Route
													path="/admin/category_console"
													element={
														<CategoryConsole />
													}
												></Route>
												<Route
													path="/admin/employee_viewer"
													element={
														<EmployeeViewer />
													}
												></Route>
												<Route
													path="/admin/role_console"
													element={<RoleConsole />}
												></Route>
												<Route
													path="/admin/job_titles_console"
													element={
														<JobTitlesConsole />
													}
												></Route>
												<Route
													path="/admin/property_console"
													element={
														<PropertyConsole />
													}
												></Route>
												<Route
													path="/admin/project_console"
													element={<ProjectConsole />}
												></Route>
												<Route
													path="/admin/phase_console"
													element={<PhaseConsole />}
												></Route>
												<Route
													path="/admin/device_status_console"
													element={
														<DeviceStatusesConsole />
													}
												></Route>
												<Route
													path="/admin/workload_viewer_console"
													element={<WorkloadViewerConsole />}
												></Route>
												<Route
													path="/admin/unit_mining_dashboard"
													element={<UnitMiningDashboard />}
												></Route>
												<Route
													path="/admin/time_off_console"
													element={<TimeOffConsole />}
												></Route>
											</>
										)}

										{/* IT Request */}
										{/* Detail Library */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Detail Library")
										) && (
											<>
												<Route
													path="/design/detail_library"
													element={<DetailLibrary />}
												></Route>
												<Route
													path="/design/detail_library/:id"
													element={
														<DetailScreen
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Detail Library"
																)?.admin_access
															)}
														/>
													}
												></Route>
											</>
										)}

										{/* Assemblies */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Assemblies")
										) && (
											<>
												<Route
													path="/design/assemblies"
													element={
														<Assemblies
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Assemblies"
																)?.admin_access
															)}
														/>
													}
												></Route>
	</>
										)}

										{/* Design Categories */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Design Categories")
										) && (
											<>
												<Route
													path="/design/design_categories"
													element={
														<DesignCategories
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Design Categories"
																)?.admin_access
															)}
														/>
													}
												></Route>
											</>
										)}

										{/* Building Elements */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Building Elements")
										) && (
											<>
												<Route
													path="/design/building_elements"
													element={<BuildingElements />}
												></Route>
											</>
										)}

										{/* Design Jobs Archive */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Design Jobs Archive")
										) && (
											<Route
												path="/design/design_jobs_archive"
												element={<DesignJobs />}
											></Route>
										)}

										{/* Specifications */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Spec Library")
										) && (
											<>
												<Route
													path="/design/scope_of_work/:id"
													element={<ScopeOfWork />}
												></Route>
												<Route
													path="/design/spec_books/:id"
													element={
														<DesignJobDetailScreen />
													}
												></Route>
												<Route
													path="/design/spec_books/:id/edit/:specID"
													element={
														<DesignJobSpecificationEdit />
													}
												></Route>
												<Route
													path="/design/spec_library"
													element={
														<SpecLibrary
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Spec Library"
																)?.admin_access
															)}
														/>
													}
												></Route>
												<Route
													path="/design/spec_library/:id"
													element={
														<SpecDetailScreen
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Spec Library"
																)?.admin_access
															)}
														/>
													}
												></Route>
												{Boolean(
													accessibleTools.find(
														(t) =>
															t.name ===
															"Spec Library"
													)?.admin_access
												) && (
													<>
														<Route
															path="/design/pending_spec_changes"
															element={
																<PendingChanges />
															}
														></Route>
														<Route
															path="/design/pending_spec_changes/:id"
															element={
																<PendingSpecChangeReviewScreen />
															}
														></Route>
														<Route
															path="/design/spec_change_log"
															element={
																<SpecificationChangeLog />
															}
														></Route>
														<Route
															path="/design/design_job_change_log"
															element={
																<DesignJobChangeLog />
															}
														></Route>
													</>
												)}
											</>
										)}

										{/* Core Repair Scopes */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Core Repair Scopes")
										) && (
											<>
												<Route
													path="/design/core_repair_scopes"
													element={
														<CoreRepairScopes
															isAdmin={Boolean(
																accessibleTools.find(
																	(t) =>
																		t.name ===
																		"Core Repair Scopes"
																)?.admin_access
															)}
														/>
													}
												></Route>
											</>
										)}

										{/* Hardware Inventory */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Hardware Inventory")
										) && (
											<>
												<Route
													path="/hardware_inventory/devices"
													element={<Devices />}
												></Route>
												<Route
													path="/hardware_inventory/leases"
													element={<DeviceLeases />}
												></Route>
												<Route
													path="/hardware_inventory/service_history"
													element={<ServiceHistory />}
												></Route>
											</>
										)}

										{/* Dev */}
										{/* <Route path="/dev" element={<Dev />}></Route> */}

										{/* Profile */}
										{Boolean(
											accessibleTools
												.map((t) => t.name)
												.includes("Email Signature")
										) && (
											<>
												<Route
													path="/profile/signature"
													element={<EmailSignature />}
												></Route>
											</>
										)}

										<Route
											path="/dropbox_analyze"
											element={
												<DropboxInterrogator
													isAdmin={Boolean(
														accessibleTools
															.map((t) => t.name)
															.includes("Admin")
													)}
												/>
											}
										></Route>

										{/* Page Not Found */}
										<Route
											path="*"
											element={<J2PageNotFound />}
										></Route>
									</Routes>
								</div>
							</div>
						</UserPreferencesProvider>
					</UserContext.Provider>
				</ThemeProvider>
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				<ThemeProvider theme={theme}>
					<Login />
				</ThemeProvider>
			</UnauthenticatedTemplate>
		</>
	);
}

export default App;
